import React, { useEffect, useState } from "react";
import NumberedHeading from "../components/NumberedHeading";
import SectionBgImg from "../components/SectionBgImg";
import { useRecoilState, useRecoilValueLoadable } from "recoil";
import { detailedResultState, resultSelector } from "../../../utils/recoil";
import config, { getNodeAssetUrl } from "../../../config";
import Cookies from "js-cookie";
import { FaSpinner } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Use always EN keys; translation for display
const DEFAULT_TRAIT_PAIRS = [
  ["INTROVERT", "EXTROVERT"],
  ["SENSING", "INTUITION"],
  ["THINKING", "FEELING"],
  ["JUDGING", "PROSPECTING"],
];

const getResultItem = (result) => (Array.isArray(result) ? result[0] : result);

const PDFResultCard = ({ resultOverride }) => {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  const { t } = useTranslation("result");
  const [data] = useRecoilState(detailedResultState);
  const resultLoadable = useRecoilValueLoadable(resultSelector);
  const result =
    resultOverride ??
    (resultLoadable.state === "hasValue" ? resultLoadable.contents : undefined);
  const resultItem = getResultItem(result);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const path = searchParams.get("path");

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "lang") {
        setLang(localStorage.getItem("lang") || "en");
      }
    };

    setLang(localStorage.getItem("lang") || "en"); // Initial set

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const downloadPDF = async () => {
    const pdfWindow = window.open("", "_blank");

    if (pdfWindow) {
      pdfWindow.document.write(`
        <html>
          <head>
            <title>Preparing PDF</title>
            <style>
              body {
                align-items: center;
                color: #1f2937;
                display: flex;
                font-family: Arial, sans-serif;
                height: 100vh;
                justify-content: center;
                margin: 0;
              }
            </style>
          </head>
          <body>Preparing your PDF...</body>
        </html>
      `);
      pdfWindow.document.close();
      pdfWindow.opener = null;
    }

    try {
      setIsLoading(true);
      const res = await fetch(
        config.nodeDomain +
          `/result/download-pdf?lang=${lang === "ar" ? "ar" : "en"}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            values: resultItem,
            mbti_type_id: data?.mbti_type_id,
            holland_code_id: data?.holland_code_id,
            id: data?.id,
            userName: Cookies.get("username", { domain: config.cookieDomain }),
            email: Cookies.get("email", { domain: config.cookieDomain }),
            student: path === "student" ? true : false,
          }),
        }
      );

      if (!res.ok) {
        throw new Error("Unable to generate PDF");
      }

      const json = await res.json();

      const windowPath = getNodeAssetUrl(json?.filePath);

      if (windowPath) {
        window.open(windowPath, "_blank");
      }
    } catch (err) {
      console.error(err);
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.close();
      }
      alert("Something went wrong. Try Again");
    } finally {
      setIsLoading(false);
    }
  };

  const traitPairsTranslated = t("pdfResultCard.traits", {
    returnObjects: true,
  });

  return (
    <div className="py-20">
      <NumberedHeading
        number={"2"}
        title={t("pdfResultCard.title")}
        className={"px-[20rem] md-max:px-[1rem]"}
      />
      <div className="my-10 px-[20rem] md-max:px-[1rem] relative">
        <SectionBgImg className="top-1/2 -translate-y-1/2" />
        <div className="bg-white shadow-xl z-10 relative rounded-xl p-5 flex flex-col md-max:items-center gap-5">
          <div className="flex gap-5 md-max:flex-col md-max:w-full">
            <div className="bg-[#F1F7FF] p-8 flex flex-col items-center gap-8 w-1/3 md-max:w-full rounded-xl">
              <p className="text-xl font-semibold">
                {t("pdfResultCard.title")}
              </p>
              <img
                src={data.image_urls ?? "/top-card-char.webp"}
                alt="profile"
                className="object-cover object-top overflow-hidden w-[8.25rem] h-[5.5rem] bg-gray-200 rounded-xl md-max:rounded-full md-max:h-[8.25rem]"
              />
              <div className="flex flex-col items-center">
                <p className="text-2xl text-primary-blue font-semibold text-center">
                  {data?.name}
                </p>
              </div>
            </div>
            <div className="w-2/3 md-max:w-full flex flex-col gap-8 justify-between">
              {resultItem?.prediction?.mbti?.values &&
                DEFAULT_TRAIT_PAIRS.map(([leftKey, rightKey], idx) => {
                  // Safely get the values
                  const mbtiValues = resultItem?.prediction?.mbti?.values;
                  const leftValue =
                    mbtiValues[leftKey] !== undefined
                      ? parseFloat(mbtiValues[leftKey])
                      : 0;
                  const rightValue =
                    mbtiValues[rightKey] !== undefined
                      ? parseFloat(mbtiValues[rightKey])
                      : 0;

                  // Get translated trait labels, fallback to key if missing
                  let leftLabel = leftKey,
                    rightLabel = rightKey;
                  if (
                    Array.isArray(traitPairsTranslated) &&
                    Array.isArray(traitPairsTranslated[idx])
                  ) {
                    leftLabel = traitPairsTranslated[idx][0];
                    rightLabel = traitPairsTranslated[idx][1];
                  }

                  return (
                    <div key={leftKey + "-" + rightKey}>
                      <progress
                        value={leftValue}
                        max="100"
                        className="h-3 w-full appearance-none rounded-full bg-primary-blue [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:bg-gray-200 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary-blue [&::-moz-progress-value]:rounded-full"
                      >
                        {leftValue}%
                      </progress>
                      <div className="flex justify-between w-full">
                        <div className="flex flex-col">
                          <p>{leftValue}%</p>
                          <p>
                            {leftLabel.charAt(0) +
                              leftLabel.slice(1).toLowerCase()}
                          </p>
                        </div>
                        <div className="flex flex-col items-end">
                          <p>{rightValue}%</p>
                          <p>
                            {rightLabel.charAt(0) +
                              rightLabel.slice(1).toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <button
            onClick={() => downloadPDF()}
            className="bg-primary-blue hover:bg-primary-blue/90 w-max rounded-[10px] py-5 px-8 text-white"
          >
            {isLoading ? (
              <FaSpinner className="animate-spin" />
            ) : (
              t("saveButton")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFResultCard;
